import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { bringWalletToFront } from '../../utils/walletConnect';

// Универсальные шаги для deposit/fee.
// step: 'connecting' | 'approve_sign' | 'approve_wait' | 'tx_sign' | 'tx_wait' | 'done'
const FLOW = {
    deposit: [
        { key: 'connecting',   title: 'Connect wallet', signLabel: 'Connecting to your wallet…',         hint: '' },
        { key: 'approve_sign', title: 'Approve token',  signLabel: 'Sign 1 of 2 — Approve token spending', hint: 'After this you will sign the actual Deposit' },
        { key: 'approve_wait', title: 'Approve token',  signLabel: 'Approve sent — waiting for blockchain', hint: 'Don’t close the app, the next signature is coming' },
        { key: 'tx_sign',      title: 'Send Deposit',   signLabel: 'Sign 2 of 2 — Confirm Deposit',       hint: 'Open your wallet again — last signature' },
        { key: 'tx_wait',      title: 'Send Deposit',   signLabel: 'Deposit sent — waiting for blockchain', hint: '' },
    ],
    fee: [
        { key: 'connecting',   title: 'Connect wallet', signLabel: 'Connecting to your wallet…',         hint: '' },
        { key: 'approve_sign', title: 'Approve fee',    signLabel: 'Sign 1 of 2 — Approve fee spending', hint: 'After this you will sign the actual Payment' },
        { key: 'approve_wait', title: 'Approve fee',    signLabel: 'Approve sent — waiting for blockchain', hint: 'Don’t close the app, the next signature is coming' },
        { key: 'tx_sign',      title: 'Pay fee',        signLabel: 'Sign 2 of 2 — Confirm Pay Fee',      hint: 'Open your wallet again — last signature' },
        { key: 'tx_wait',      title: 'Pay fee',        signLabel: 'Payment sent — waiting for blockchain', hint: '' },
    ],
    // Одна подпись, без approve — контракт просто отдаёт остаток
    withdraw: [
        { key: 'connecting',   title: 'Connect wallet', signLabel: 'Connecting to your wallet…',          hint: '' },
        { key: 'tx_sign',      title: 'Withdraw',       signLabel: 'Sign — Confirm Withdraw',             hint: 'Open your wallet to sign' },
        { key: 'tx_wait',      title: 'Withdraw',       signLabel: 'Withdraw sent — waiting for blockchain', hint: '' },
    ],
};

// Только эти шаги показываем пользователю как «вехи» (3 кружка).
// approve_sign + approve_wait схлопываются в одну веху, и так же для tx_*.
const MILESTONES = ['connecting', 'approve_sign', 'tx_sign'];
// У withdraw нет approve — вехи короче
const MILESTONES_WITHDRAW = ['connecting', 'tx_sign'];

function getMilestoneIndex(step, type) {
    if (type === 'withdraw') {
        if (step === 'done') return MILESTONES_WITHDRAW.length;
        if (step === 'tx_sign' || step === 'tx_wait') return 1;
        return 0;
    }
    if (step === 'done') return MILESTONES.length;
    if (step === 'connecting') return 0;
    if (step === 'approve_sign' || step === 'approve_wait') return 1;
    if (step === 'tx_sign' || step === 'tx_wait') return 2;
    return 0;
}

function isWaitingForUser(step) {
    return step === 'approve_sign' || step === 'tx_sign';
}

function isWaitingForChain(step) {
    return step === 'approve_wait' || step === 'tx_wait';
}

function CheckIcon() {
    return (
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="20 6 9 17 4 12" />
        </svg>
    );
}

function WalletPokeIcon() {
    return (
        <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
            <rect x="2" y="6" width="20" height="14" rx="3" />
            <path d="M16 13h2" />
            <path d="M2 10h20" />
        </svg>
    );
}

function ErrorIcon() {
    return (
        <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="10" />
            <line x1="12" y1="8" x2="12" y2="12" />
            <line x1="12" y1="16" x2="12.01" y2="16" />
        </svg>
    );
}

function SuccessIcon() {
    return (
        <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
            <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
            <polyline points="22 4 12 14.01 9 11.01" />
        </svg>
    );
}

// status: { type: 'deposit'|'fee', step, error?: string } | null
export default function TxProgressOverlay({ status, onClose, onRetry }) {
    const isOpen = !!status;
    const type = status?.type || 'deposit';
    const step = status?.step || 'connecting';
    const error = status?.error || '';
    const flow = FLOW[type] || FLOW.deposit;
    const milestones = type === 'withdraw' ? MILESTONES_WITHDRAW : MILESTONES;
    const currentIdx = getMilestoneIndex(step, type);
    const current = flow.find(s => s.key === step) || flow[0];
    const done = step === 'done';
    const titleByType = type === 'fee' ? 'Pay Fee' : type === 'withdraw' ? 'Withdraw' : 'Deposit';

    return (
        <AnimatePresence>
            {isOpen && (
                <motion.div
                    className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center bg-black/80 backdrop-blur-md"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                >
                    <motion.div
                        className="w-full max-w-[420px] rounded-t-[24px] sm:rounded-[24px] bg-[#1C1C1E] border border-white/10 px-5 pt-6 pb-6"
                        initial={{ y: 40, opacity: 0 }}
                        animate={{ y: 0, opacity: 1 }}
                        exit={{ y: 40, opacity: 0 }}
                        transition={{ type: 'spring', damping: 28, stiffness: 280 }}
                    >
                        {/* Header */}
                        <div className="text-center mb-5">
                            <h3 className="text-white text-[18px] font-bold">{titleByType}</h3>
                            <p className="text-white/40 text-[12px] mt-0.5">
                                {error ? 'Something went wrong' : done ? 'All done' : 'Follow the steps in your wallet'}
                            </p>
                        </div>

                        {/* Banner when transitioning to second signature — ловит внимание юзера */}
                        {step === 'tx_sign' && !error && type !== 'withdraw' && (
                            <motion.div
                                initial={{ opacity: 0, y: -8, scale: 0.95 }}
                                animate={{ opacity: 1, y: 0, scale: 1 }}
                                transition={{ type: 'spring', damping: 18, stiffness: 260 }}
                                className="mb-4 rounded-2xl border border-emerald-400/30 bg-emerald-500/10 px-4 py-3 flex items-center gap-3"
                            >
                                <div className="w-7 h-7 rounded-full bg-emerald-500 text-white flex items-center justify-center flex-shrink-0">
                                    <CheckIcon />
                                </div>
                                <div className="flex-1 leading-tight">
                                    <p className="text-emerald-200 text-[13px] font-semibold">Step 1 of 2 done</p>
                                    <p className="text-emerald-200/70 text-[11px] mt-0.5">Now open your wallet again for the second signature</p>
                                </div>
                            </motion.div>
                        )}

                        {/* Big status icon area */}
                        <div className="flex flex-col items-center justify-center py-3 mb-4 min-h-[120px]">
                            {error ? (
                                <div className="text-rose-400">
                                    <ErrorIcon />
                                </div>
                            ) : done ? (
                                <motion.div
                                    initial={{ scale: 0.5, opacity: 0 }}
                                    animate={{ scale: 1, opacity: 1 }}
                                    transition={{ type: 'spring', damping: 14 }}
                                    className="text-emerald-400"
                                >
                                    <SuccessIcon />
                                </motion.div>
                            ) : isWaitingForUser(step) ? (
                                <motion.div
                                    animate={{ scale: [1, 1.08, 1] }}
                                    transition={{ duration: 1.4, repeat: Infinity, ease: 'easeInOut' }}
                                    className="text-amber-300"
                                >
                                    <WalletPokeIcon />
                                </motion.div>
                            ) : (
                                <div className="w-12 h-12 border-[3px] border-white/15 border-t-[#3478F6] rounded-full animate-spin" />
                            )}

                            {/* Sub-text under icon */}
                            <div className="mt-4 text-center px-2">
                                {error ? (
                                    <p className="text-rose-300 text-[14px] leading-snug break-words">
                                        {error}
                                    </p>
                                ) : (
                                    <>
                                        <p className={`text-[15px] font-semibold ${
                                            isWaitingForUser(step) ? 'text-amber-200' : 'text-white'
                                        }`}>
                                            {current.signLabel}
                                        </p>
                                        {current.hint && (
                                            <p className={`text-[12px] mt-1.5 ${
                                                isWaitingForUser(step) ? 'text-amber-100/70' : 'text-white/50'
                                            }`}>
                                                {current.hint}
                                            </p>
                                        )}
                                        {isWaitingForChain(step) && (
                                            <p className="text-white/35 text-[11px] mt-1">
                                                This can take 10–30 seconds
                                            </p>
                                        )}
                                        {isWaitingForUser(step) && (
                                            <button
                                                onClick={() => bringWalletToFront()}
                                                className="mt-3 px-4 py-2 rounded-[10px] border border-white/20 text-[13px] font-semibold text-[#EAEAEA] transition active:bg-white/5"
                                            >
                                                Open wallet
                                            </button>
                                        )}
                                    </>
                                )}
                            </div>
                        </div>

                        {/* Milestones */}
                        <div className="flex items-center justify-between mb-5 px-2">
                            {milestones.map((mKey, i) => {
                                const stepInfo = flow.find(f => f.key === mKey);
                                const isDone = done || i < currentIdx;
                                const isActive = !done && i === currentIdx && !error;
                                return (
                                    <React.Fragment key={mKey}>
                                        <div className="flex flex-col items-center gap-1.5 flex-shrink-0">
                                            <div className={`w-7 h-7 rounded-full flex items-center justify-center text-[11px] font-bold transition-colors ${
                                                isDone
                                                    ? 'bg-emerald-500 text-white'
                                                    : isActive
                                                        ? 'bg-[#3478F6] text-white'
                                                        : 'bg-white/10 text-white/40'
                                            }`}>
                                                {isDone ? <CheckIcon /> : i + 1}
                                            </div>
                                            <span className={`text-[10px] text-center leading-tight max-w-[72px] ${
                                                isActive ? 'text-white' : 'text-white/40'
                                            }`}>
                                                {stepInfo?.title}
                                            </span>
                                        </div>
                                        {i < milestones.length - 1 && (
                                            <div className={`flex-1 h-[2px] mx-1 mb-5 rounded-full transition-colors ${
                                                i < currentIdx || done ? 'bg-emerald-500' : 'bg-white/10'
                                            }`} />
                                        )}
                                    </React.Fragment>
                                );
                            })}
                        </div>

                        {/* Actions */}
                        {error ? (
                            <div className="flex flex-col gap-2">
                                {onRetry && (
                                    <button
                                        onClick={onRetry}
                                        className="w-full rounded-2xl bg-[#3478F6] py-3.5 text-[15px] font-semibold text-white active:opacity-80"
                                    >
                                        Try again
                                    </button>
                                )}
                                <button
                                    onClick={onClose}
                                    className="w-full rounded-2xl border border-white/10 py-3 text-[14px] text-white/60 active:text-white"
                                >
                                    Close
                                </button>
                            </div>
                        ) : done ? (
                            <button
                                onClick={onClose}
                                className="w-full rounded-2xl bg-emerald-500 py-3.5 text-[15px] font-semibold text-white active:opacity-80"
                            >
                                Done
                            </button>
                        ) : (
                            <p className="text-center text-white/30 text-[11px]">
                                Don’t close the app — the transaction is in progress
                            </p>
                        )}
                    </motion.div>
                </motion.div>
            )}
        </AnimatePresence>
    );
}
